import { useMemo } from 'react'

import { lensToBreakdown } from 'features/catalog/utils/charts-v2-panels'
// bully ignore[no-cross-feature-private-imports: the established per-panel chart-data fetch every v2-native page (charts-v2 itself, catalog's own detail-page sentiment charts via charts-v2-panels.ts) uses; there is no public seam for it yet. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useChartQuery } from 'features/charts-v2/hooks/use-chart-query'
import { METRICS } from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'

export type CatalogRowMetrics = {
  sentences: number | null
  sentiment: number | null
  rating: number | null
}

/**
 * Per-row Sentences/Sentiment/Rating for the current lens, keyed by the
 * row's `value`. Fetched via the same `/charts/chart` group-by aggregate
 * `buildSentimentVolumePanel` (charts-v2-panels.ts) already uses for the
 * catalog detail page's own sentiment charts — `lensToBreakdown` applies the
 * identical grouping rule `build-catalog-rows.ts` uses for row identity
 * (grouped for theme when `groupThemes`, ungrouped otherwise), so a
 * breakdown's `category[0]` string lines up with `FocusListOption.value`.
 * When themes are grouped, that means parent rows only — a grouped row's
 * `children` (subthemes) have no matching breakdown value and show "–".
 *
 * Excludes `source_type`: an ungrouped source row can be per-(source,
 * country) or a syndicated source, neither of which lines up with a single
 * `source_type` breakdown value — see the `enabled` gate below.
 */
export function useCatalogTableMetrics(params: {
  projectId: string
  selectedLens: string
  panel: ChartPanel
  /** The table this feeds (`CatalogTable`) isn't rendered on a detail page —
   * defaults to `true` for every other caller. */
  enabled?: boolean
  /** Whether the theme lens's rows are grouped (parent rollups) or ungrouped
   * (one row per subtheme) — metrics must request the matching breakdown
   * granularity or every row's lookup key misses. Ignored by every other
   * lens, which is always ungrouped here regardless of this flag. Defaults
   * to `true` (today's only behavior, before the "Group themes" toggle). */
  groupThemes?: boolean
}): { metricsByValue: Record<string, CatalogRowMetrics>; isLoading: boolean } {
  const { projectId, selectedLens, panel, enabled = true } = params
  const groupThemes = params.groupThemes ?? true

  const metricsPanel = useMemo<ChartPanel>(
    () => ({
      ...panel,
      metrics: [
        METRICS.SENTENCE_COUNT.id,
        METRICS.SENTIMENT.id,
        METRICS.RATING.id,
      ],
      breakdowns: [
        lensToBreakdown(selectedLens, selectedLens === 'theme' && groupThemes),
      ],
    }),
    [panel, selectedLens, groupThemes],
  )

  const isEnabled = enabled && selectedLens !== 'source_type'
  const { data, isLoading: rawIsLoading } = useChartQuery(
    projectId,
    metricsPanel,
    { enabled: isEnabled },
  )
  // A disabled react-query v3 query never leaves `status: 'loading'`, so
  // `rawIsLoading` would report `true` forever for the excluded source_type
  // lens — gate it on `isEnabled` too.
  const isLoading = isEnabled && rawIsLoading

  const metricsByValue = useMemo(() => {
    const map: Record<string, CatalogRowMetrics> = {}
    for (const row of data?.data ?? []) {
      const value = String(row.category[0])
      map[value] = {
        sentences: row.values[0] ?? null,
        sentiment: row.values[1] ?? null,
        rating: row.values[2] ?? null,
      }
    }
    return map
  }, [data])

  return { metricsByValue, isLoading }
}
